import {
    Injectable, inject
} from "@angular/core";
import {
    HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse
} from "@angular/common/http";
import {
    Observable, throwError, timer, MonoTypeOperatorFunction, OperatorFunction
} from "rxjs";
import {
    catchError,
    retry
} from "rxjs/operators";
import { NetworkConnectivityException } from "../exceptions/network-connectivity-exception";
import { DEFAULT_EXCEPTION_MESSAGE } from "../exceptions/constants/exception-constants";
import { HttpClientException } from "../exceptions/http-client-exception";
import { COMMON_APP_SERVICE } from "@shopliftr/common-ng";

/**
 * This interceptor processes http requests, responses, and errors.
 * It should be provided first, so that it is the first to process the request
 * and the last interceptor to handle errors.
 */
@Injectable()
export class CommonHttpInterceptor implements HttpInterceptor {

    /**
     * If an error can be retried it will happen a maximum of 3 times
     */
    static readonly MAX_RETRY_ATTEMPTS = 3;

    /**
     * For each retry attempt the system will wait for 300ms multiplied by the attempt before making another attempt
     * i.e. on the first attempt it will wait 300ms, the second 600ms, and so forth.
     */
    static readonly RETRY_DURATION_FACTOR = 300;
    private readonly _app = inject(COMMON_APP_SERVICE);

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

        return next.handle(request).pipe(
            this._processError(),
            this._retryHttp()
        );
    }

    /** Take the HttpError Response and create a CoreUiException and send it to the ICommonErrorHandlerService */
    private _processError(): OperatorFunction<HttpEvent<any>, HttpEvent<any>> {

        return catchError((error: unknown) => {

            if (!(error instanceof Error)) {
                switch ((error as HttpErrorResponse)?.status) {
                    case 0:
                    // The http client will sometime return a 0 if there is a network failure
                    case 502:
                    case 503:
                    case 504:
                        error = new NetworkConnectivityException("Failed to connect to the server. Please try again.", error);
                        break;
                    default:
                        error = new HttpClientException((error as Error)?.message ?? DEFAULT_EXCEPTION_MESSAGE, error);
                }
            }
            this._app.errorHandler.handleHttpClientError(error);
            // The error handler will log the error if necessary
            return throwError(() => error);
        });
    }

    private _retryHttp(): MonoTypeOperatorFunction<HttpEvent<any>> {

        return retry<HttpEvent<any>>({
            count: CommonHttpInterceptor.MAX_RETRY_ATTEMPTS,
            delay: (error: any, retryAttempt: number) => {

                // if maximum number of retries have been met
                // or response is a status code we don't wish to retry, throw error
                if (!(error instanceof NetworkConnectivityException)) {
                    return throwError(() => error);
                }

                if (retryAttempt === 1) {
                    this._app.notificationService.displayWarningMessage("Currently experiencing network connectivity issues. Retry the transaction. This may take longer than normal", undefined, 1800);
                }
                // Retry after 300ms, 600ms, 900ms, etc;
                const waitTime = retryAttempt * CommonHttpInterceptor.RETRY_DURATION_FACTOR;
                this._app.logger.logWarn(
                    `Network Connection Error: ${error.message}. Attempt ${retryAttempt}: retrying in ${waitTime}ms`,
                    error
                );
                return timer(waitTime);
            }
        });
    }
}
